<template>
  <div>
    <div>
      <div class="font-weight-bold">
        {{ $t('shop.reports.name') }}:
      </div>
      <BFormInput v-model="reportName" />
    </div>
    <div>
      <div class="font-weight-bold mt-2">
        {{ $t('shop.reports.fileType') }}:
      </div>
      <BFormSelect
        v-model="fileType"
        :options="fileTypeOptions"
      />
    </div>
    <hr class="mb-0">
    <div
      v-if="catalogDetails.orderSchema === 'Daily'"
      class="d-flex align-items-center py-2 group-row"
      @click="hideDay = !hideDay"
    >
      <Checkbox
        :value="hideDay"
        :emit="false"
      />
      <span class="font-weight-bold">
        Ukryj dzień
      </span>
    </div>
    <div
      class="d-flex align-items-center py-2 group-row"
      @click="reportUnpaid = !reportUnpaid"
    >
      <Checkbox
        :value="reportUnpaid"
        :emit="false"
      />
      <span class="font-weight-bold">
        {{ $t('shop.showUnpaid') }}
      </span>
    </div>
    <div
      v-if="reportUnpaid"
      class="d-flex align-items-center py-2 ml-4 group-row"
      @click="reportIsPaid = !reportIsPaid"
    >
      <Checkbox
        :value="reportIsPaid"
        :emit="false"
      />
      <span class="font-weight-bold">
        Pokaż czy opłacone
      </span>
    </div>
    <div
      class="d-flex align-items-center py-2 group-row"
      @click="reportPrice = !reportPrice"
    >
      <Checkbox
        :value="reportPrice"
        :emit="false"
      />
      <span class="font-weight-bold">
        {{ $t('shop.showPrice') }}
      </span>
    </div>
    <div
      v-if="catalogDetails.productCategory === 'Catering'"
      class="d-flex align-items-center py-2 group-row"
      @click="reportMeal = !reportMeal"
    >
      <Checkbox
        :value="reportMeal"
        :emit="false"
      />
      <span class="font-weight-bold">
        Pokaż jadłospis
      </span>
    </div>
    <div
      v-if="catalogDetails.orderSchema === 'Single'"
      class="d-flex align-items-center py-2 group-row"
      @click="reportAll = !reportAll"
    >
      <Checkbox
        :value="reportAll"
        :emit="false"
      />
      <span class="font-weight-bold">
        Pokaż wszystkie zamówienia
      </span>
    </div>
    <div
      class="d-flex align-items-center py-2 group-row"
      @click="productsInColumns = !productsInColumns"
    >
      <Checkbox
        :value="productsInColumns"
        :emit="false"
      />
      <span class="font-weight-bold">
        {{ $t('shop.productsInColumns') }}
      </span>
    </div>
    <div
      v-if="!productsInColumns"
      class="d-flex align-items-center py-2 group-row"
      @click="hideProducts = !hideProducts"
    >
      <Checkbox
        :value="hideProducts"
        :emit="false"
      />
      <span class="font-weight-bold">
        Ukryj produkty
      </span>
    </div>
    <div class="font-weight-bold mt-2">
      Pokaż dane oferty:
    </div>
    <div>
      <div
        v-for="s in catalogDetails.subCatalogs.filter(x => !x.subCatalogId.startsWith('#'))"
        :key="s.id"
        class="d-flex align-items-center group-row"
        @click="toggleSubCatalog(s.subCatalogId)"
      >
        <Checkbox
          :value="subCatalogs.includes(s.subCatalogId)"
          :emit="false"
        />

        {{ s.name }}
      </div>
    </div>

    <div class="font-weight-bold mt-2">
      {{ $t('shop.reports.filterGroups') }}:
    </div>
    <GroupSelect
      :group-codes.sync="groups"
      show-select-all
      allow-rule-based-select
    />
    <div class="font-weight-bold mt-3">
      {{ $t('shop.reports.includeProperties') }}:
    </div>
    <div class="row">
      <div class="col-6">
        <div>
          {{ $t('shop.reports.includedProperties') }}
        </div>
        <Draggable
          v-model="includeProperties"
          class="draggable"
          ghost-class="ghost"
          group="columns"
          :class="{ moving }"
          @start="moving = true"
          @end="moving = false"
        >
          <div
            v-for="c in includeProperties"
            :key="c"
            class="column"
          >
            <i :class="icon(c)" />
            {{ name(c) }}
          </div>
        </Draggable>
      </div>
      <div class="col-6">
        <div>
          {{ $t('shop.reports.availableProperties') }}
        </div>
        <Draggable
          v-model="otherProperties"
          class="draggable"
          ghost-class="ghost"
          group="columns"
          :class="{ moving }"
          @start="moving = true"
          @end="moving = false"
        >
          <div
            v-for="c in otherProperties"
            :key="c.key"
            class="column"
          >
            <i :class="icon(c)" />
            {{ name(c) }}
          </div>
        </Draggable>
      </div>
    </div>

    <hr>
    <SwitchWithDescription
      :title="$t('shop.reports.send')"
      small
      class="mb-1"
      style="min-height: 44px; margin-top: 0"
      :value="emailEnabled"
      @switch="emailEnabled = !emailEnabled"
    />

    <div v-if="emailEnabled">
      <div class="font-weight-bold mt-3">
        {{ $t('shop.reports.emails') }} ({{ $t('shop.reports.lineSeparated') }}):
      </div>
      <BFormTextarea
        v-model="emails"
        :placeholder="$t('shop.reports.emailsInLines')"
        rows="3"
      />
      <DeliveryTimeSelect
        class="mt-2"
        month-delivery
        style="margin-left: -1rem; margin-right: -1rem"
        :hour.sync="hour"
        :skip-weekend.sync="skipWeekend"
        :day-offset.sync="dayOffset"
        :type.sync="emailScopeType"
      />
    </div>
  </div>
</template>

<script>
import GroupSelect from '@/components/payers/form/GroupSelect';
import SwitchWithDescription from '@/components/settings/SwitchWithDescription';
import Draggable from 'vuedraggable';
import { mapGetters } from 'vuex';
import DeliveryTimeSelect from '../DeliveryTimeSelect';

export default {
  props: {
    subscription: Object,
  },
  data: () => ({
    emails: '',
    moving: false,
    groups: [],
    hour: '9:00',
    emailScopeType: 'day',
    dayOffset: 0,
    skipWeekend: false,
    includeProperties: [],
    otherProperties: [],
    subCatalogs: [],
    reportUnpaid: false,
    reportPrice: false,
    reportIsPaid: false,
    reportMeal: false,
    hideDay: false,
    productsInColumns: false,
    hideProducts: false,
    pending: false,
    reportAll: false,
    sortBy: [],
    reportName: '',
    emailEnabled: false,
    fileType: 'xlsx',
  }),
  components: {
    SwitchWithDescription,
    DeliveryTimeSelect,
    Draggable,
    GroupSelect,
  },
  computed: {
    ...mapGetters([
      'payee',
      'userGroups',
      'properties',
    ]),
    ...mapGetters('shop', [
      'catalogDetails',
      'singleOrderSchema',
    ]),
    extendedProperties() {
      return [
        {
          name: this.$t('general.name'),
          key: 'name',
          type: 'default',
        },
        {
          name: this.$t('client.clientCode'),
          key: 'code',
          type: 'default',
        },
        {
          name: this.$t('client.number'),
          key: 'number',
          type: 'default',
        },
        ...this.properties
          .map((x) => ({
            name: x.name,
            key: x.code,
            type: 'property',
          }))
          .sort((a, b) => a.name.localeCompare(b.name))];
    },
    fileTypeOptions() {
      return [
        { value: 'xlsx', text: this.$t('shop.reports.fileTypeXlsx') },
        { value: 'pdf', text: this.$t('shop.reports.fileTypePdf') },
      ];
    },
    config() {
      return {
        name: this.reportName,
        fileType: this.fileType,
        properties: this.includeProperties,
        hideDay: this.hideDay,
        hideProducts: this.hideProducts,
        reportMeal: this.reportMeal && this.catalogDetails.productCategory === 'Catering',
        reportIsPaid: this.reportUnpaid && this.reportIsPaid,
        reportUnpaid: this.reportUnpaid,
        subCatalogs: this.subCatalogs,
        reportPrice: this.reportPrice,
        productsInColumns: this.productsInColumns,
        hour: this.hour,
        skipWeekend: this.skipWeekend,
        dayOffset: parseInt(this.dayOffset, 10),
        groups: this.groups,
        emailDisabled: !this.emailEnabled,
        sorted: true,
        reportAll: this.reportAll,
        emailScopeType: this.emailScopeType,
        emails: this.emails.split('\n')
          .filter((x) => x && x.includes('@'))
          .map((x) => x.trim()),
      };
    },
  },
  watch: {
    config(c) {
      this.$emit('update', c);
    },
  },
  methods: {
    toggleSubCatalog(subCatalogId) {
      if (this.subCatalogs.includes(subCatalogId)) {
        this.subCatalogs = this.subCatalogs.filter((x) => x !== subCatalogId);
      } else {
        this.subCatalogs.push(subCatalogId);
      }
    },
    name(key) {
      return this.extendedProperties.find((x) => x.key === key)?.name;
    },
    icon(key) {
      const column = this.extendedProperties.find((x) => x.key === key)?.type;
      if (column === 'category') {
        return 'far fa-box-dollar pr-1';
      }
      if (column === 'group') {
        return 'far fa-users pr-1';
      }
      if (column === 'property') {
        return 'far far fa-tag pr-1';
      }

      return null;
    },
  },
  created() {
    if (this.subscription) {
      this.groups = this.subscription.config.groups;
      this.includeProperties = this.subscription.config.properties;
      this.hour = this.subscription.config.hour;
      this.reportName = this.subscription.config.name;
      this.fileType = this.subscription.config.fileType || 'xlsx';
      this.reportPrice = this.subscription.config.reportPrice;
      this.reportIsPaid = this.subscription.config.reportIsPaid;
      this.hideDay = this.subscription.config.hideDay;
      this.reportAll = this.subscription.config.reportAll;
      this.emailScopeType = this.subscription.config.emailScopeType || 'day';
      this.hideProducts = this.subscription.config.hideProducts;
      this.subCatalogs = this.subscription.config.subCatalogs || [];
      this.reportMeal = this.subscription.config.reportMeal;
      this.productsInColumns = this.subscription.config.productsInColumns;
      this.reportType = this.subscription.type;
      this.dayOffset = this.subscription.config.dayOffset || 0;
      this.skipWeekend = this.subscription.config.skipWeekend || false;
      this.reportUnpaid = this.subscription.config.reportUnpaid;
      this.emails = this.subscription.config.emails.join('\n');
      this.emailEnabled = !this.subscription.config.emailDisabled;

      if (!this.subscription.config.sorted) {
        const defaultSort = this.extendedProperties.map((x) => x.key);

        this.includeProperties = this.includeProperties
          .sort((a, b) => defaultSort.indexOf(a) - defaultSort.indexOf(b));
      }
    } else {
      this.includeProperties = ['name'];
    }

    this.otherProperties = this.extendedProperties
      .filter((x) => !this.includeProperties.includes(x.key))
      .map((x) => x.key);
  },
};
</script>

<style lang="scss" scoped>
  .group-row {
    border-bottom: 1px solid #eee;
    padding-top: 5px;
    padding-bottom: 5px;
    cursor: pointer;

    &:hover {
      background-color: #efefef;
    }
  }

  .close-button {
    position: absolute;
  }

  .draggable {
    border: 1px solid transparent;
    border-radius: 10px;
    min-height: 50px;
    background-color: #f8f8f8;
    padding: 5px;
    margin-top: 5px;
    min-height: 200px;
    padding-bottom: 40px;
  }

  .draggable.moving {
    border: 1px solid #aaa;
  }

  .moving * {
    cursor: grabbing;
  }

  .column {
    padding: 0.3rem 0.6rem;
    width: 100%;
    border-radius: 10px;
    background-color: #fff;
    border: 2px solid #eee;
    margin-top: 6px;
    font-size: 1rem;
    line-height: 1.2;
    cursor: pointer;
    transition: border-color 300ms, margin-right 300ms;

    &:hover {
      border-color: #999;
    }
  }
</style>
